import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { getDjangoEndpoint } from "src/utils/apiEndpoints";
import { toApiError } from "src/types/api";
import axiosInstance from "src/store/features/auth/axiosCSRF";
// 파일 정보 타입 정의
interface FileInfo {
  file_name: string;
  file_extension: string;
  file_size: number;
  s3_path: string;
}

// workFlowFilterCallbackAPI 파라미터 타입 정의
interface WorkFlowFilterCallbackParams {
  sample_use?: boolean;
  library?: string | null;
  target_file_info?: FileInfo | null;
  drug_file_info?: FileInfo | null;
  assay_file_info?: FileInfo | null;
  residues?: string[] | null;
  filter_adme_threshold?: number | null;
  filter_druglikeness?: any;
  filter_plip?: any;
  protein_name?: string | null;
  protein_seq?: string | null;
  chain?: string | null;
  ligand_id?: string | null;
  assay_id?: string | null;
  gt_label?: string | null;
  assay_smiles_col?: string | null;
  num_mol?: number | null;
  [key: string]: any;
}

// WorkFlowFileCallBackAPI 파라미터 타입 정의
export interface WorkflowGetCallBackFileList {
  group_name: string;
  library_name: string;
  round: number;
  workflow_name?: string;
  file_type?: string;
  sample_use?: boolean;
  saveToTargetPdbSave?: boolean;
  saveToMimicPdbSave?: boolean;
  saveToAssayCsvSave?: boolean;
}

// 통합된 iUserParameterState 인터페이스
export interface iUserPPiParameterState {
  library: string | null;
  target_file: File | null;
  target_file_type: string | null;
  target_file_info: FileInfo | null;
  drug_file_context: File | null;
  drug_file : File | null;
  drug_file_info: FileInfo | null;
  assay_file: File | null;
  assay_file_type: string | null;
  assay_file_info: FileInfo | null;
  residues: string[] | null;
  assay_id: string | null;
  gt_label: string | null;
  assay_smiles_col: string | null;
  filter_druglikeness: {
    drug_filter: {
      [key: string]: any;
    };
    min_hba_hbd_sum: number | null;
    qeppi_threshold: number | null;
    sa_threshold: number | null;
  };
  bioactivity_filter: {
    apply_bioactivity_filter: boolean;
    bioactivity_types: string[];
    exclude_metal: boolean;
    apply_structural_filter: boolean;
    structural_filters: {
      [key: string]: [number | null, number | null];
    };
  };
  filter_plip: {
    [key: string]: any;
  };
  hasPlipData: boolean;
  filter_adme_threshold: number | null;
  compoundColumn: string | null;
  smilesColumn: string | null;
  affinityColumn: string | null;
  molecularNumber: number | null;
  target_chain: string | null;
  target_ligand_id: string | null;
  drug_receptor_chain_id: string | null;
  drug_chain_ligand_id: string | null;
  protein_name: string | null;
  protein_seq: string | null;
  target_all_residue: string[];
  selected_plip_residues: string[];
  userChoiceStep: number;
  coordinates: number[];
  chain: string | null;
  ligand_id: string | null;
  userChoiceDockingBoxTab: string | null;
  userFileChecking: string | null;
  round2Enabled: boolean;
  round2EnabledLibraries: any;
  round2FakeRun: boolean;
  userInputWorkSpaceName: string;
  userChoiceTool?: string;
  userChoiceKinaseTarget: string;
  userChoiceKinaseOffTargetList?: string[];
  userToolRunningStatus: {
    [toolName: string]: {
      isRunning: boolean;
      error?: string;
    };
  };
  userHaveAssayFile: boolean;
  userChoiceToolType:string;
  userDontKnowResidueState: boolean;
  
  // workFlowFileCallback에서 추가된 상태들
  response: any;
  TargetPdbResponse: any;
  MimicPdbResponse: any | null;
  AssayCsvResponse: any;
  isLoading: boolean;
  message: string | null;
  status: number | null;
  // 개별 로딩 상태
  isTargetLoading: boolean;
  isMimicLoading: boolean;
  isAssayLoading: boolean;
  error: string | null;
  showNextRoundAssayModal: boolean;
  userRunningStep: number;
  autoStep: string | null;
  cardExpanded: boolean;
  setFullNameTargetLigandId: string | null;
  docking_smiles: string | null;
}

const isEmptyCallbackData = (data: any) => {
  if (data == null) return true;
  if (typeof data === "string") return data.trim().length === 0;
  if (Array.isArray(data)) return data.length === 0;
  if (typeof data === "object") return Object.keys(data).length === 0;
  return false;
};

const clearAssayState = (state: iUserPPiParameterState) => {
  state.AssayCsvResponse = null;
  state.assay_file = null;
  state.assay_file_type = null;
  state.assay_file_info = null;
  state.userHaveAssayFile = false;
  state.compoundColumn = null;
  state.smilesColumn = null;
  state.affinityColumn = null;
  state.assay_id = null;
  state.gt_label = null;
  state.assay_smiles_col = null;
  state.isAssayLoading = false;
};

// 초기 상태
const initialState: iUserPPiParameterState = {
  library: null,
  target_file: null,
  target_file_type: null,
  target_file_info: null,
  drug_file: null,
  drug_file_context: null,
  drug_file_info: null,
  assay_file: null,
  assay_file_type: null,
  assay_file_info: null,
  residues: null,
  assay_id: null,
  gt_label: null,
  assay_smiles_col: null,
  filter_druglikeness: {
    drug_filter: {},
    min_hba_hbd_sum: null,
    qeppi_threshold: null,
    sa_threshold: null,
  },
  bioactivity_filter: {
    apply_bioactivity_filter: false,
    bioactivity_types: ['IC50', 'Kd', 'Ki', 'GI50', 'EC50', 'AC50'],
    exclude_metal: false,
    apply_structural_filter: false,
    structural_filters: {
      "MW": [null, null],
      "HBA": [null, null],
      "HBD": [null, null],
      "NUM_AROMRINGS": [null, null]
    }
  },
  filter_plip: {
    plip: {}
  },
  hasPlipData: false,
  filter_adme_threshold: null,
  smilesColumn: null,
  affinityColumn: null,
  compoundColumn: null,
  molecularNumber: null,
  target_chain: null,
  target_ligand_id: null,
  drug_receptor_chain_id: null,
  drug_chain_ligand_id: null,
  protein_name: null,
  protein_seq: null,
  target_all_residue: [],
  selected_plip_residues: [],
  userChoiceStep: 9,
  coordinates: [],
  chain: null,
  ligand_id: null,
  userChoiceDockingBoxTab: null,
  userFileChecking: null,
  round2Enabled: false,
  round2EnabledLibraries: {},
  round2FakeRun: false,
  userInputWorkSpaceName: "",
  userChoiceTool: null,
  userChoiceKinaseTarget: "",
  userChoiceKinaseOffTargetList: [],
  userToolRunningStatus: {},
  userHaveAssayFile: false,
  userChoiceToolType : "",
  userDontKnowResidueState: false,
  
  // workFlowFileCallback에서 추가된 초기값들
  response: null,
  TargetPdbResponse: null,
  MimicPdbResponse: null,
  AssayCsvResponse: null,
  isLoading: false,
  message: "",
  status: null,
  isTargetLoading: false,
  isMimicLoading: false,
  isAssayLoading: false,
  error: null,
  showNextRoundAssayModal: false,
  userRunningStep: null,
  autoStep: null,
  cardExpanded: false,
  setFullNameTargetLigandId: null,
  docking_smiles: null,
};

// 임시저장 Filter Callback 
export const temporarySaveFilterCallbackAPI = createAsyncThunk(
  "userParameter/temporarySaveFilterCallback",
  async (params: WorkFlowFilterCallbackParams = {
    residues: null
  }, { rejectWithValue }) => {
    try {
      const djangoEndpoint = getDjangoEndpoint();
      const pathParam = "/workflow/temp-callback/";

      if (!djangoEndpoint) {
        throw new Error("Django endpoint not found");
      }

      const requestBody = Object.keys(params).length > 0 ? params : {};

      const response = await axiosInstance.post(
        `${djangoEndpoint}${pathParam}`,
        requestBody,
        { withCredentials: true }
      );

      return response.data;
    } catch (error) {
      const apiError = toApiError(error);
      return rejectWithValue(apiError.response?.data || apiError.message);
    }
  }
);

export const temporarySaveFileCallBackAPI = createAsyncThunk(
  "userParameter/temporarySaveFileCallBack",
  async (data: WorkflowGetCallBackFileList, { rejectWithValue }) => {
    try {
      const djangoEndpoint = getDjangoEndpoint();
      const pathParam = '/workflow/temp-file-callback/';
      
      const requestData: any = {
        group_name: data.group_name,
        library_name: data.library_name,
        workflow_name: data.workflow_name,
        round: data.round,
        file_type: data.file_type,
      };

      // sample_use 명확하게 전달
      if (data.sample_use !== undefined) {
        requestData.sample_use = data.sample_use;
      }
      
      const response = await axiosInstance.post(`${djangoEndpoint}${pathParam}`, requestData);
      
      return {
        sample_use: Boolean(data.sample_use), // 요청한 값 그대로 반환
        data: response.data,
        status: response.status
      };
    } catch (error) {
      console.error('❌ temporarySaveFileCallBackAPI Error:', error);
      return rejectWithValue(error);
    }
  }
);

// workFlowFilterCallbackAPI - 기존 유지
export const workFlowFilterCallbackAPI = createAsyncThunk(
  "userParameter/workFlowFilterCallback",
  async (params: WorkFlowFilterCallbackParams = {
    residues: null
  }, { rejectWithValue }) => {
    try {
      const djangoEndpoint = getDjangoEndpoint();
      const pathParam = "/workflow/filter-callback/";

      if (!djangoEndpoint) {
        throw new Error("Django endpoint not found");
      }

      const { restore_assay_info: _restoreAssayInfo, ...apiParams } = params;
      const requestBody = Object.keys(apiParams).length > 0 ? apiParams : {};

      const response = await axiosInstance.post(
        `${djangoEndpoint}${pathParam}`,
        requestBody,
        { withCredentials: true }
      );

      return response.data;
    } catch (error) {
      const apiError = toApiError(error);
      return rejectWithValue(apiError.response?.data || apiError.message);
    }
  }
);

// WorkFlowFileCallBackAPI - userParameter로 통합
export const WorkFlowFileCallBackAPI = createAsyncThunk(
  "userParameter/WorkFlowFileCallBack",
  async (data: WorkflowGetCallBackFileList, { rejectWithValue }) => {
    try {
      const djangoEndpoint = getDjangoEndpoint();
      const pathParam = '/workflow/file-callback/';

      const requestData: any = {
        group_name: data.group_name,
        library_name: data.library_name,
        workflow_name: data.workflow_name,
        round: data.round,
        file_type: data.file_type,
      };

      // sample_use가 제공된 경우에만 추가
      if (data.sample_use !== undefined) {
        requestData.sample_use = data.sample_use;
      }
      
      const response = await axiosInstance.post(`${djangoEndpoint}${pathParam}`, requestData);
      
      // saveToPdbResponse 플래그를 통해 반환 데이터에 메타데이터 추가
      return {
        saveToTargetPdbResponse: data.saveToTargetPdbSave || false,
        saveToMimicPdbResponse: data.saveToMimicPdbSave || false,
        saveToAssayCsvResponse: data.saveToAssayCsvSave || false,
        data: response.data,
        status: response.status
      };
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

// UserParameter Slice - 통합된 버전
export const userParameterSlice = createSlice({
  name: "userParameter",
  initialState,
  reducers: {
    setLibrary: (state, action) => {
      const newLibrary = action.payload;

      if (state.library !== newLibrary) {
        const round2Status = state.round2EnabledLibraries[newLibrary] || false;
        
        // PDB 응답 데이터 백업
        const preservedData = {
          TargetPdbResponse: state.TargetPdbResponse,
          MimicPdbResponse: state.MimicPdbResponse,
          AssayCsvResponse: state.AssayCsvResponse,
        };

        Object.assign(state, {
          ...initialState,
          library: newLibrary,
          round2Enabled: round2Status,
          round2EnabledLibraries: state.round2EnabledLibraries,
          // PDB 응답 데이터 복원
          ...preservedData,
        });
      }
    },
    setTargetFile: (state, action) => {
      state.target_file = action.payload;
    },
    setTargetFileType: (state, action) => {
      state.target_file_type = action.payload;
    },
    setDrugFile: (state, action) => {
      state.drug_file = action.payload;
    },
    setDrugFileInfo: (state, action) => {
      state.drug_file_info = action.payload;
    },
    setAssayFile: (state, action) => {
      state.assay_file = action.payload;
    },
    setAssayFileType: (state, action) => {
      state.assay_file_type = action.payload;
    },
    setResidues: (state, action: PayloadAction<string[] | null>) => {
      state.residues = action.payload;
    },
    setAssayId: (state, action) => {
      state.assay_id = action.payload;
    },
    setGtLabel: (state, action) => {
      state.gt_label = action.payload;
    },
    setAssaySmileCol: (state, action) => {
      state.assay_smiles_col = action.payload;
    },
    setAutoStepSave: (state, action) => {
      state.autoStep = action.payload;
    },
    setRound2EnabledLibraries: (state, action) => {
      state.round2EnabledLibraries = action.payload;
    },
    setDruglikeness: (state, action) => {
      if (action.payload) {
        if (action.payload.drug_filter) {
          state.filter_druglikeness = {
            ...state.filter_druglikeness,
            drug_filter: { ...action.payload.drug_filter }
          };
        }
        
        if (action.payload.min_hba_hbd_sum !== undefined) {
          state.filter_druglikeness.min_hba_hbd_sum = action.payload.min_hba_hbd_sum;
        }
        
        if (action.payload.qeppi_threshold !== undefined) {
          state.filter_druglikeness.qeppi_threshold = action.payload.qeppi_threshold;
        }
        
        if (action.payload.sa_threshold !== undefined) {
          state.filter_druglikeness.sa_threshold = action.payload.sa_threshold;
        }
      }
    },
    setBioactivityFilter: (state, action) => {
      state.bioactivity_filter = action.payload;
    },
    setPlip: (state, action) => {
      state.filter_plip = action.payload;

      const selectedResidues: string[] = [];

      if (action.payload && action.payload.plip) {
        Object.entries(action.payload.plip).forEach(
          ([interactionType, params]) => {
            if (
              params &&
              typeof params === "object" &&
              "residues" in params &&
              params.residues
            ) {
              selectedResidues.push(params.residues as string);
            }
          }
        );
      }

      state.selected_plip_residues = selectedResidues;
    },
    clearPlipData: (state) => {
      state.filter_plip = { plip: {} };
      state.hasPlipData = false;
      state.selected_plip_residues = [];
    },
    setAdme: (state, action) => {
      state.filter_adme_threshold = action.payload;
    },
    setSmilesColumn: (state, action) => {
      state.smilesColumn = action.payload;
    },
    setAffinityColumn: (state, action) => {
      state.affinityColumn = action.payload;
    },
    setCompoundColumn: (state, action) => {
      state.compoundColumn = action.payload;
    },
    setMolecularNumber: (state, action) => {
      state.molecularNumber = action.payload;
    },
    setTargetChainID: (state, action) => {
      state.target_chain = action.payload;
    },
    setTargetLigandID: (state, action) => {
      state.target_ligand_id = action.payload;
    },
    setDrugReceptorChainID: (state, action) => {
      state.drug_receptor_chain_id = action.payload;
    },
    setDrugChainLigandID: (state, action) => {
      state.drug_chain_ligand_id = action.payload;
    },
    setLocationXYZ: (state, action) => {
      state.coordinates = action.payload;
    },
    setProteinName: (state, action) => {
      state.protein_name = action.payload;
    },
    setProteinSequence: (state, action) => {
      state.protein_seq = action.payload;
    },
    setAllResidueList: (state, action) => {
      state.target_all_residue = action.payload;
    },
    setSelectedPlipResidues: (state, action) => {
      state.selected_plip_residues = action.payload;
    },
    setUserChoiceStep: (state, action) => {
      state.userChoiceStep = action.payload;
    },
    setUserChain: (state, action) => {
      state.chain = action.payload;
    },
    setUserLigandID: (state, action) => {
      state.ligand_id = action.payload;
    },
    setUserChoiceDockingTab: (state, action) => {
      state.userChoiceDockingBoxTab = action.payload;
    },
    setUserFileUploadChecking: (state, action) => {
      state.userFileChecking = action.payload;
    },
    setUserRunningStep: (state, action) => {
      state.userRunningStep = action.payload;
    },
    resetAllParametersExceptLibrary: (state, action) => {
      const libraryName = action.payload;
      const round2Status = state.round2EnabledLibraries[libraryName] || false;

      Object.assign(state, {
        ...initialState,
        library: libraryName,
        round2Enabled: round2Status,
        round2EnabledLibraries: state.round2EnabledLibraries,
      });
    },
    resetAllParameters: (state) => {
      const currentLibrary = state.library;
      const currentRound2Enabled = state.round2Enabled;
      const currentRound2EnabledLibraries = state.round2EnabledLibraries;

      Object.assign(state, {
        ...initialState,
        library: currentLibrary,
        round2Enabled: currentRound2Enabled,
        round2EnabledLibraries: currentRound2EnabledLibraries,
      });
    },
    resetAllFileInfo: (state) => {
      state.target_file = null;
      state.drug_file = null;
      state.drug_file_context = null;
      state.assay_file = null;
      state.target_file_type = null;
      state.assay_file_type = null;
      state.target_file_info = null;
      state.drug_file_info = null;
      state.assay_file_info = null;
      state.userFileChecking = null;
      state.userHaveAssayFile = false;
      state.userChoiceToolType = null
    },
    setRound2EnabledForLibrary: (state, action) => {
      const libraryName = action.payload;
      state.round2EnabledLibraries[libraryName] = true;
      if (state.library === libraryName) {
        state.round2Enabled = true;
      }
    },
    setRound2FakeRun: (state, action) => {
      state.round2FakeRun = action.payload;
    },
    setUserChoiceTool: (state, action) => {
      state.userChoiceTool = action.payload;
    },
    setUserInputWorkSpaceName: (state, action) => {
      state.userInputWorkSpaceName = action.payload;
    },
    setUserChoiceKinaseTarget: (state, action) => {
      state.userChoiceKinaseTarget = action.payload;
    },
    setCardExpanded: (state, action) => {
      state.cardExpanded = action.payload;
    },
    setUserChoiceKinaseOffTargetList: (state, action) => {
      state.userChoiceKinaseOffTargetList = action.payload;
    },
    setUserToolRunningStatus: (state, action) => {
      const { libraryName, isRunning, error } = action.payload;
      state.userToolRunningStatus[libraryName] = { isRunning, error };
    },
    setUserHaveAssayFile: (state, action) => {
      state.userHaveAssayFile = action.payload;
    },
    setUserChoiceToolType : (state, action) => {
      state.userChoiceToolType = action.payload;
    },
    setUserDontKnowResidueState: (state, action) => {
      state.userDontKnowResidueState = action.payload;
    },
    resetAllExceptLibrary: (state) => {
      const currentLibrary = state.library;
      Object.assign(state, {
        ...initialState,
        library: currentLibrary,
      });
    },
    setTargetFileInfo: (state, action) => {
      state.target_file_info = action.payload;
    },
    setAssayFileInfo: (state, action) => {
      state.assay_file_info = action.payload;
    },
    setPdbLigandResponse: (state, action: PayloadAction<any>) => {
      state.MimicPdbResponse = action.payload;
      state.AssayCsvResponse = action.payload;
      state.TargetPdbResponse = action.payload;
    },
    setTargetPdbResponse: (state, action: PayloadAction<any>) => {
      state.TargetPdbResponse = action.payload;
    },
    setMimicPdbResponse: (state, action: PayloadAction<any>) => {
      state.MimicPdbResponse = action.payload;
    },
    setAssayCsvResponse: (state, action: PayloadAction<any>) => {
      state.AssayCsvResponse = action.payload;
    },
    clearAssayCallbackState: (state) => {
      clearAssayState(state);
    },
    resetWorkflowResult: (state) => {
      state.response = null;
      state.status = null;
      state.message = "";
      state.isLoading = false;
    },
    resetWorkFlowFileCallback: (state) => {
      state.response = null;
      state.TargetPdbResponse = null;
      state.MimicPdbResponse = null;
      state.AssayCsvResponse = null;
      state.target_file = null;
      state.target_file_type = null;
      state.target_file_info = null;
      state.drug_file = null;
      state.drug_file_context = null;
      state.drug_file_info = null;
      state.assay_file = null;
      state.assay_file_type = null;
      state.assay_file_info = null;
      state.isLoading = false;
      state.message = "";
      state.status = null;
      state.isTargetLoading = false;
      state.isMimicLoading = false;
      state.isAssayLoading = false;
    },
    resetTargetPdbResponse: (state) => {
      state.TargetPdbResponse = null;
      state.isTargetLoading = false;
    },
    resetMimicPdbResponse: (state) => {
      state.MimicPdbResponse = null;
      state.isMimicLoading = false;
    },
    clearAssayFileData: (state) => {
      clearAssayState(state);
    },
    clearWorkflowError: (state) => {
      state.message = "";
      state.status = null;
      state.error = null;
    },
    setShowNextRoundAssayModal: (state, action: PayloadAction<boolean>) => {
      state.showNextRoundAssayModal = action.payload;
    },
    setUserTargetLigandFullName: (state, action: PayloadAction<string>) => {
      state.setFullNameTargetLigandId = action.payload;
    },
    resetAssayFileInfo: (state) => {
      clearAssayState(state);
    },
    resetForSampleData: (state) => {
      state.target_file_info = null;
      state.drug_file_info = null;
      state.assay_file_info = null;
      state.target_file = null;
      state.drug_file_context = null;
      state.assay_file = null;
      
      // Residue 관련 초기화
      state.residues = null;
      
      state.filter_druglikeness = {
        drug_filter: {},
        min_hba_hbd_sum: null,
        qeppi_threshold: null,
        sa_threshold: null,
      };
      state.filter_adme_threshold = null;
      
      state.filter_plip = { plip: {} };
      state.hasPlipData = false;
      state.selected_plip_residues = [];
      
      state.protein_name = null;
      state.protein_seq = null;
      state.target_chain = null;
      state.target_ligand_id = null;
      state.chain = null;
      state.ligand_id = null;
      
      state.compoundColumn = null;
      state.affinityColumn = null;
      state.smilesColumn = null;
      state.assay_id = null;
      state.gt_label = null;
      state.assay_smiles_col = null;
      
      state.molecularNumber = null;
    },
    setDockingSmiles: (state, action: PayloadAction<string>) => {
      state.docking_smiles = action.payload;
    }
  },
  extraReducers: (builder) => {
    builder
      // ==================== temporarySaveFilterCallbackAPI ====================
      .addCase(temporarySaveFilterCallbackAPI.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(temporarySaveFilterCallbackAPI.fulfilled, (state, action) => {
        state.isLoading = false;
        state.error = null;
        const response = action.payload;

        if (response?.drug_file_name && response?.drug_file_ext) {
          state.drug_file_info = {
            file_name: response.drug_file_name,
            file_extension: response.drug_file_ext,
            file_size: 0,
            s3_path: response.drug_s3_path || undefined,
          };
        }
        
        if (response?.target_file_name && response?.target_file_ext) {
          state.target_file_info = {
            file_name: response.target_file_name,
            file_extension: response.target_file_ext,
            file_size: 0,
            s3_path: response.target_s3_path || undefined,
          };
        }
        
        if (response?.assay_file_name && response?.assay_file_ext) {
          state.assay_file_info = {
            file_name: response.assay_file_name,
            file_extension: response.assay_file_ext,
            file_size: 0,
            s3_path: response.assay_s3_path || undefined,
          };
          state.userHaveAssayFile = true;
        }

        // Residue 정보 업데이트 (배열 타입)
        if (response?.residues && state.residues === null) {
          state.residues = response.residues;
        }

        if (response?.filter_adme_threshold !== undefined && state.filter_adme_threshold === null) {
          state.filter_adme_threshold = response.filter_adme_threshold;
        }

        // Druglikeness 필터 업데이트
        if (response?.filter_druglikeness) {
          const apiDruglikeness = response.filter_druglikeness;
          
          if (apiDruglikeness.drug_filter) {
            state.filter_druglikeness.drug_filter = { ...apiDruglikeness.drug_filter };
          }
          
          if (apiDruglikeness.min_hba_hbd_sum !== undefined) {
            state.filter_druglikeness.min_hba_hbd_sum = apiDruglikeness.min_hba_hbd_sum;
          }
          
          if (apiDruglikeness.qeppi_threshold !== undefined) {
            state.filter_druglikeness.qeppi_threshold = apiDruglikeness.qeppi_threshold;
          }
          
          if (apiDruglikeness.sa_threshold !== undefined) {
            state.filter_druglikeness.sa_threshold = apiDruglikeness.sa_threshold;
          }
        }

        // PLIP 필터 업데이트
        if (response?.filter_plip) {
          state.filter_plip = response.filter_plip;
          state.hasPlipData = true;

          const selectedResidues: string[] = [];
          if (response.filter_plip.plip) {
            Object.entries(response.filter_plip.plip).forEach(
              ([interactionType, params]) => {
                if (
                  params &&
                  typeof params === "object" &&
                  "residue" in params &&
                  params.residue
                ) {
                  selectedResidues.push(params.residue as string);
                }
              }
            );
          }
          state.selected_plip_residues = selectedResidues;
        }

        // 기타 정보들 업데이트
        if (response?.protein_name && state.protein_name === null) {
          state.protein_name = response.protein_name;
        }
        if (response?.protein_seq && state.protein_seq === null) {
          state.protein_seq = response.protein_seq;
        }
        if (response?.chain && state.target_chain === null) {
          state.target_chain = response.chain;
        }
        if (response?.ligand_id && state.target_ligand_id === null) {
          state.target_ligand_id = response.ligand_id;
        }
        const hasRestorableAssayInfo = Boolean(response?.assay_file_info);

        if (!hasRestorableAssayInfo) {
          clearAssayState(state);
        }

        if (hasRestorableAssayInfo && response?.assay_id && state.compoundColumn === null) {
          state.compoundColumn = response.assay_id;
        }
        if (hasRestorableAssayInfo && response?.gt_label && state.affinityColumn === null) {
          state.affinityColumn = response.gt_label;
        }
        if (hasRestorableAssayInfo && response?.assay_smiles_col && state.smilesColumn === null) {
          state.smilesColumn = response.assay_smiles_col;
        }
        if (response?.coordinates && state.coordinates.length === 0) {
          state.coordinates = response.coordinates;
        }
        
        if (response?.num_mol !== undefined) {
          state.molecularNumber = response.num_mol;
        }
      })
      .addCase(temporarySaveFilterCallbackAPI.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
    
      // ==================== temporarySaveFileCallBackAPI ====================
      .addCase(temporarySaveFileCallBackAPI.pending, (state) => {
        state.isLoading = true;
        state.status = null;
        state.message = "";
      })
      .addCase(temporarySaveFileCallBackAPI.fulfilled, (state, action) => {
        state.isLoading = false;
        state.status = action.payload.status;
        
        const requestData = action.meta.arg;
        const responseData = action.payload.data;
        
        const fileType = requestData.file_type;
        
        if (fileType === 'target') {
          state.TargetPdbResponse = responseData;
          state.isTargetLoading = false;
        } else if (fileType === 'mimic' || fileType === 'drug') {
          state.MimicPdbResponse = responseData;
          state.isMimicLoading = false;
        } else if (fileType === 'assay') {
          if (isEmptyCallbackData(responseData)) {
            clearAssayState(state);
          } else {
            state.AssayCsvResponse = responseData;
            state.isAssayLoading = false;
          }
        } else {
          state.response = responseData;
        }
      })
      .addCase(temporarySaveFileCallBackAPI.rejected, (state, action) => {
        state.isLoading = false;
        
        const errorPayload = action.payload as any;
        const requestData = action.meta.arg;
        
        console.error('temporarySaveFileCallBackAPI Error:', {
          requestData,
          errorPayload
        });
        
        if (typeof errorPayload === "object" && errorPayload !== null) {
          state.message = errorPayload.error?.message || "An error occurred";
          state.status = errorPayload.error?.response?.status || null;
        } else {
          state.message = (errorPayload as string) || "An error occurred";
        }
      })
    
      // workFlowFilterCallbackAPI extraReducers
      .addCase(workFlowFilterCallbackAPI.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(workFlowFilterCallbackAPI.fulfilled, (state, action) => {
        state.isLoading = false;
        state.error = null;
        const response = action.payload;
        
        const requestParams = action.meta.arg;
        const isSampleData = requestParams?.sample_use === true;

        // 파일 정보 업데이트
        if (response?.drug_file_info) {
          if (isSampleData || state.drug_file_info === null) {
            state.drug_file_info = response.drug_file_info;
          }
        }
        
        if (response?.target_file_info) {
          if (isSampleData || state.target_file_info === null) {
            state.target_file_info = response.target_file_info;
          }
        }
        
        const shouldRestoreAssayInfo = requestParams?.restore_assay_info !== false;

        const hasRestorableAssayInfo = Boolean(shouldRestoreAssayInfo && response?.assay_file_info);

        if (hasRestorableAssayInfo) {
          state.assay_file_info = response.assay_file_info;
          state.userHaveAssayFile = true;
        } else if (shouldRestoreAssayInfo) {
          clearAssayState(state);
        }

        // Residue 정보 업데이트 (배열 타입)
        if (response?.residues) {
          if (isSampleData || state.residues === null) {
            state.residues = response.residues;
          }
        }

        if (response?.filter_adme_threshold !== undefined) {
          if (isSampleData || state.filter_adme_threshold === null) {
            state.filter_adme_threshold = response.filter_adme_threshold;
          }
        }

        // Druglikeness 필터
        if (response?.filter_druglikeness) {
          const apiDruglikeness = response.filter_druglikeness;
          
          if (isSampleData) {
            if (apiDruglikeness.drug_filter) {
              state.filter_druglikeness.drug_filter = { ...apiDruglikeness.drug_filter };
            }
            
            if (apiDruglikeness.min_hba_hbd_sum !== undefined) {
              state.filter_druglikeness.min_hba_hbd_sum = apiDruglikeness.min_hba_hbd_sum;
            }
            
            if (apiDruglikeness.qeppi_threshold !== undefined) {
              state.filter_druglikeness.qeppi_threshold = apiDruglikeness.qeppi_threshold;
            }
            
            if (apiDruglikeness.sa_threshold !== undefined) {
              state.filter_druglikeness.sa_threshold = apiDruglikeness.sa_threshold;
            }
          } else {
            if (apiDruglikeness.drug_filter) {
              state.filter_druglikeness.drug_filter = { ...apiDruglikeness.drug_filter };
            }
            
            if (apiDruglikeness.min_hba_hbd_sum !== undefined) {
              state.filter_druglikeness.min_hba_hbd_sum = apiDruglikeness.min_hba_hbd_sum;
            }
            
            if (apiDruglikeness.qeppi_threshold !== undefined) {
              state.filter_druglikeness.qeppi_threshold = apiDruglikeness.qeppi_threshold;
            }
            
            if (apiDruglikeness.sa_threshold !== undefined) {
              state.filter_druglikeness.sa_threshold = apiDruglikeness.sa_threshold;
            }
          }
        }

        // PLIP 필터
        if (response?.filter_plip) {
          state.filter_plip = response.filter_plip;
          state.hasPlipData = true;

          const selectedResidues: string[] = [];
          if (response.filter_plip.plip) {
            Object.entries(response.filter_plip.plip).forEach(
              ([interactionType, params]) => {
                if (
                  params &&
                  typeof params === "object" &&
                  "residue" in params &&
                  params.residue
                ) {
                  selectedResidues.push(params.residue as string);
                }
              }
            );
          }
          state.selected_plip_residues = selectedResidues;
        }

        // 기타 정보들
        if (response?.protein_name !== undefined) {
          if (isSampleData || state.protein_name === null) {
            state.protein_name = response.protein_name;
          }
        }
        if (response?.protein_seq !== undefined) {
          if (isSampleData || state.protein_seq === null) {
            state.protein_seq = response.protein_seq;
          }
        }
        if (response?.chain !== undefined) {
          if (isSampleData || state.target_chain === null) {
            state.target_chain = response.chain;
          }
        }
        if (response?.ligand_id !== undefined) {
          state.target_ligand_id = response.ligand_id;
        }
        if (hasRestorableAssayInfo && response?.assay_id !== undefined) {
          state.compoundColumn = response.assay_id;
        }
        if (hasRestorableAssayInfo && response?.gt_label !== undefined) {
          state.affinityColumn = response.gt_label;
        }
        if (hasRestorableAssayInfo && response?.assay_smiles_col !== undefined) {
          state.smilesColumn = response.assay_smiles_col;
        }
        if (response?.coordinates !== undefined) {
          if (isSampleData || state.coordinates === null || state.coordinates.length === 0) {
            state.coordinates = response.coordinates;
          }
        }
        
        if (response?.num_mol !== undefined) {
          state.molecularNumber = response.num_mol;
        }
      })
      .addCase(workFlowFilterCallbackAPI.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })

      // WorkFlowFileCallBackAPI extraReducers
      .addCase(WorkFlowFileCallBackAPI.pending, (state, action) => {
        state.isLoading = true;
        state.status = null;
        state.message = "";
        
        const { saveToTargetPdbSave, saveToMimicPdbSave, saveToAssayCsvSave } = action.meta.arg;
        
        if (saveToTargetPdbSave) {
          state.isTargetLoading = true;
        }
        if (saveToMimicPdbSave) {
          state.isMimicLoading = true;
        }
        if (saveToAssayCsvSave) {
          state.isAssayLoading = true;
        }
      })
      .addCase(WorkFlowFileCallBackAPI.fulfilled, (state, action) => {
        state.isLoading = false;
        state.status = action.payload.status;
        
        if (action.payload.saveToMimicPdbResponse) {
          state.MimicPdbResponse = action.payload.data;
          state.isMimicLoading = false;
        } else if (action.payload.saveToAssayCsvResponse) {
          if (isEmptyCallbackData(action.payload.data)) {
            clearAssayState(state);
          } else {
            state.AssayCsvResponse = action.payload.data;
            state.isAssayLoading = false;
            state.userHaveAssayFile = true;
          }
        } else if (action.payload.saveToTargetPdbResponse) {
          state.TargetPdbResponse = action.payload.data;
          state.isTargetLoading = false;
        } else {
          state.response = action.payload.data;
        }
      })
      .addCase(WorkFlowFileCallBackAPI.rejected, (state, action) => {
        state.isLoading = false;
        
        const errorPayload = action.payload as any;
        
        if (typeof errorPayload === 'object' && errorPayload !== null) {
          state.message = errorPayload.error?.message || "An error occurred";
          state.status = errorPayload.error?.response?.status || null;
          
          const requestMeta = errorPayload.requestMeta;
          if (requestMeta) {
            if (requestMeta.saveToTargetPdbSave) {
              state.isTargetLoading = false;
            }
            if (requestMeta.saveToMimicPdbSave) {
              state.isMimicLoading = false;
            }
            if (requestMeta.saveToAssayCsvSave) {
              state.isAssayLoading = false;
            }
          }
        } else {
          state.message = errorPayload as string || "An error occurred";
        }
      });
  },
});

export const {
  setUserChain,
  setUserLigandID,
  setUserFileUploadChecking,
  setLocationXYZ,
  setLibrary,
  setTargetFile,
  setTargetFileType,
  setAssayFileType,
  setDrugFile,
  setAssayFile,
  setResidues,
  setAssayId,
  setGtLabel,
  setAssaySmileCol,
  setDruglikeness,
  resetAllFileInfo,
  setBioactivityFilter,
  setPlip,
  clearPlipData,
  setAdme,
  setSmilesColumn,
  setAffinityColumn,
  setCompoundColumn,
  setProteinName,
  setProteinSequence,
  setMolecularNumber,
  setTargetChainID,
  setTargetLigandID,
  setDrugReceptorChainID,
  setDrugChainLigandID,
  setAllResidueList,
  setSelectedPlipResidues,
  setUserChoiceStep,
  setUserChoiceDockingTab,
  resetAllParametersExceptLibrary,
  resetAllParameters,
  setRound2EnabledForLibrary,
  setRound2FakeRun,
  setUserChoiceTool,
  setUserInputWorkSpaceName,
  setUserChoiceKinaseTarget,
  setUserChoiceKinaseOffTargetList,
  setUserToolRunningStatus,
  resetAssayFileInfo,
  setUserHaveAssayFile,
  setUserDontKnowResidueState,
  resetAllExceptLibrary,
  setTargetFileInfo,
  setDrugFileInfo,
  setAssayFileInfo,
  setRound2EnabledLibraries,
  setPdbLigandResponse,
  setTargetPdbResponse,
  setMimicPdbResponse,
  setAssayCsvResponse,
  clearAssayCallbackState,
  resetWorkflowResult,
  resetWorkFlowFileCallback,
  resetTargetPdbResponse,
  resetMimicPdbResponse,
  clearAssayFileData,
  clearWorkflowError,
  resetForSampleData,
  setShowNextRoundAssayModal,
  setUserRunningStep,
  setAutoStepSave,
  setCardExpanded,
  setUserChoiceToolType,
  setUserTargetLigandFullName,
  setDockingSmiles
} = userParameterSlice.actions;

export default userParameterSlice.reducer;
